/**
 * PDFExportModal.js
 * Export configuration modal:
 *   - Select "Export All Pages" or "Custom Page Range"
 *   - Shows page pills preview of selected pages
 *   - Calls pdfExport utility with progress feedback
 */
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { exportToPDF, parsePageRange } from './pdfExport';
import styles from './PDFExportModal.module.css';

export default function PDFExportModal({ onClose, sheetRefs, totalPages, filename = 'Study-Notes.pdf' }) {
  const [mode, setMode] = useState('all'); // 'all' | 'custom'
  const [customRange, setCustomRange] = useState('');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState('');

  // Compute which page indices are selected
  const selectedIndices = useMemo(() => {
    if (mode === 'all') {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    return parsePageRange(customRange, totalPages);
  }, [mode, customRange, totalPages]);

  const handleExport = async () => {
    if (selectedIndices.length === 0) {
      setError('No valid pages selected.');
      return;
    }

    setExporting(true);
    setError('');
    setProgress({ current: 0, total: selectedIndices.length });

    try {
      const refs = sheetRefs.map(r => r?.current).filter(Boolean);
      await exportToPDF(
        refs,
        selectedIndices,
        filename,
        (current, total) => setProgress({ current, total })
      );
      // Brief success state then close
      setTimeout(() => {
        setExporting(false);
        onClose();
      }, 600);
    } catch (err) {
      console.error('PDF export failed:', err);
      setError('Export failed. Please try again.');
      setExporting(false);
    }
  };

  const progressPercent = progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <AnimatePresence>
      <motion.div
        className={styles.overlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      >
        <motion.div
          className={styles.modal}
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 10 }}
          transition={{ type: 'spring', stiffness: 340, damping: 28 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.titleWrap}>
              <div className={styles.titleIcon}>📄</div>
              <div>
                <div className={styles.title}>Export to PDF</div>
                <div className={styles.subtitle}>Pixel-perfect A4 (210mm × 297mm)</div>
              </div>
            </div>
            <button className={styles.closeBtn} onClick={onClose}>✕</button>
          </div>

          {/* Body */}
          <div className={styles.body}>
            {/* Page range selector */}
            <div>
              <div className={styles.sectionLabel}>Page Selection</div>
              <div className={styles.radioGroup}>
                <label
                  className={`${styles.radioOption} ${mode === 'all' ? styles.radioOptionSelected : ''}`}
                  onClick={() => setMode('all')}
                >
                  <input
                    type="radio"
                    className={styles.radioInput}
                    checked={mode === 'all'}
                    onChange={() => setMode('all')}
                  />
                  <div>
                    <div className={styles.radioLabel}>Export All Pages</div>
                    <div className={styles.radioDesc}>
                      All {totalPages} page{totalPages !== 1 ? 's' : ''} will be included
                    </div>
                  </div>
                </label>

                <label
                  className={`${styles.radioOption} ${mode === 'custom' ? styles.radioOptionSelected : ''}`}
                  onClick={() => setMode('custom')}
                >
                  <input
                    type="radio"
                    className={styles.radioInput}
                    checked={mode === 'custom'}
                    onChange={() => setMode('custom')}
                  />
                  <div style={{ flex: 1 }}>
                    <div className={styles.radioLabel}>Custom Page Range</div>
                    <div className={styles.radioDesc}>Specify pages manually</div>
                    {mode === 'custom' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.18 }}
                        style={{ marginTop: 8 }}
                      >
                        <input
                          className={styles.rangeInput}
                          placeholder="e.g. 1, 3 or 2-4 or 1, 3-5"
                          value={customRange}
                          onChange={e => setCustomRange(e.target.value)}
                          autoFocus
                        />
                        <div className={styles.rangeHint}>
                          Use commas to separate pages, dashes for ranges (e.g. 1, 3-5)
                        </div>
                      </motion.div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Page pills preview */}
            {selectedIndices.length > 0 && (
              <div>
                <div className={styles.sectionLabel}>
                  Selected: {selectedIndices.length} page{selectedIndices.length !== 1 ? 's' : ''}
                </div>
                <div className={styles.pagePreview}>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <div
                      key={i}
                      className={`${styles.pagePill} ${selectedIndices.includes(i) ? styles.pagePillSelected : ''}`}
                    >
                      P{i + 1}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progress bar */}
            {exporting && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className={styles.progressWrap}>
                  <div className={styles.progressBar} style={{ width: `${progressPercent}%` }} />
                </div>
                <div className={styles.progressLabel}>
                  Rendering page {progress.current} of {progress.total}...
                </div>
              </motion.div>
            )}

            {error && (
              <p style={{ fontSize: '0.72rem', color: '#ef5350', margin: 0 }}>{error}</p>
            )}

            {/* Export button */}
            <button
              className={styles.exportBtn}
              onClick={handleExport}
              disabled={exporting || selectedIndices.length === 0}
            >
              {exporting ? (
                <><span className={styles.spinner} /> Generating PDF...</>
              ) : (
                <>📥 Download PDF ({selectedIndices.length} page{selectedIndices.length !== 1 ? 's' : ''})</>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
